import { Building2, Plus, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { EmptyState } from '@/components/ui/empty-state';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { LoadingPage } from '@/components/ui/loading';
import { formatDate } from '@/lib/utils';
import { useOrganizationsPage } from '../hooks/use-organizations-page';
import { CreateOrganizationModal } from './create-organization-modal';

export function OrganizationsPage() {
  const navigate = useNavigate();
  const {
    organizations,
    loading,
    totalCount,
    totalPages,
    page,
    searchInput,
    setSearchInput,
    handlePageChange,
    isCreateModalOpen,
    setIsCreateModalOpen,
    handleCreateOrganization,
  } = useOrganizationsPage();

  if (loading) return <LoadingPage />;

  return (
    <>
      <div className="p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="relative max-w-xs w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                className="pl-9"
                placeholder="Search organizations..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
            </div>
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="w-4 h-4" />
              Create Organization
            </Button>
          </div>

          {/* Table */}
          <Card>
            {organizations.length === 0 ? (
              <EmptyState
                icon={<Building2 className="w-8 h-8" />}
                title="No organizations found"
                description={searchInput ? 'Try adjusting your search.' : 'Create your first organization to get started.'}
              />
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Slug</TableHead>
                      <TableHead className="text-right">Users</TableHead>
                      <TableHead className="text-right">Properties</TableHead>
                      <TableHead className="text-right">Devices</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {organizations.map(org => (
                      <TableRow
                        key={org.id}
                        className="cursor-pointer"
                        onClick={() => navigate(`/organizations/${org.id}`)}
                      >
                        <TableCell className="font-medium">{org.name}</TableCell>
                        <TableCell className="text-gray-500 dark:text-gray-400 font-mono text-sm">{org.slug}</TableCell>
                        <TableCell className="text-right">{org.userCount}</TableCell>
                        <TableCell className="text-right">{org.propertyCount}</TableCell>
                        <TableCell className="text-right">{org.deviceCount}</TableCell>
                        <TableCell className="text-gray-500 dark:text-gray-400">{formatDate(org.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400">{totalCount} organizations</p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => handlePageChange(page - 1)}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        Page {page} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages}
                        onClick={() => handlePageChange(page + 1)}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      </div>

      <CreateOrganizationModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateOrganization}
      />
    </>
  );
}
